import React, { useState, useEffect } from 'react';
import { X, Save, ShieldAlert } from 'lucide-react';
import { InstalledModuleRecord } from './ModuleRegistry.js';

interface ModuleSettingsModalProps {
  moduleRecord: InstalledModuleRecord;
  isOpen: boolean;
  onClose: () => void;
}

export const ModuleSettingsModal: React.FC<ModuleSettingsModalProps> = ({
  moduleRecord,
  isOpen,
  onClose,
}) => {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen, moduleRecord.id]);

  const getStorageKey = () => `mod_settings_${moduleRecord.id}`;

  const loadSettings = async () => {
    try {
      // In a real app, this might load from IndexedDB. 
      // For this implementation we use localStorage for simplicity as requested, 
      // or we can use indexedDB. Using localStorage for immediate synchronous load.
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        setSettings(JSON.parse(stored));
      } else {
        const defaultSettings: Record<string, any> = {};
        const schema = moduleRecord.settingsSchema || {};
        for (const key of Object.keys(schema)) {
          if (schema[key]?.default !== undefined) {
            defaultSettings[key] = schema[key].default;
          }
        }
        setSettings(defaultSettings);
      }
    } catch (err) {
      console.error('Failed to load settings', err);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(settings));
      // Dispatch an event so the module knows settings changed
      window.dispatchEvent(
        new CustomEvent('module-settings-changed', {
          detail: { moduleId: moduleRecord.id, settings },
        })
      );
      setTimeout(() => {
        setIsSaving(false);
        onClose();
      }, 300);
    } catch (err) {
      console.error('Failed to save settings', err);
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const schema = moduleRecord.settingsSchema;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-[#0f172a] border border-[#1e293b] rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1e293b] bg-[#0b0f19]">
          <div>
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              {moduleRecord.name} Settings
            </h2>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{moduleRecord.id} • {moduleRecord.version}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {!schema || Object.keys(schema).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500">
              <ShieldAlert className="w-10 h-10 mb-3 opacity-50" />
              <p className="text-sm">This module does not expose any configurable settings.</p>
            </div>
          ) : (
            Object.entries(schema).map(([key, prop]: [string, any]) => (
              <div key={key} className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-slate-300">
                  {key}
                  {prop.sensitive && <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-mono uppercase">Sensitive</span>}
                </label>
                {prop.description && (
                  <p className="text-xs text-slate-500">{prop.description}</p>
                )}
                
                {prop.type === 'boolean' ? (
                  <div className="mt-1 flex items-center">
                    <button
                      onClick={() => setSettings(s => ({ ...s, [key]: !s[key] }))}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings[key] ? 'bg-sky-500' : 'bg-slate-700'}`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings[key] ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <span className="ml-3 text-sm text-slate-400">{settings[key] ? 'Enabled' : 'Disabled'}</span>
                  </div>
                ) : prop.type === 'number' ? (
                  <input
                    type="number"
                    value={settings[key] || 0}
                    onChange={(e) => setSettings(s => ({ ...s, [key]: Number(e.target.value) }))}
                    className="w-full bg-[#0b0f19] border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500 transition-colors"
                  />
                ) : prop.enum ? (
                  <select
                    value={settings[key] || ''}
                    onChange={(e) => setSettings(s => ({ ...s, [key]: e.target.value }))}
                    className="w-full bg-[#0b0f19] border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500 transition-colors"
                  >
                    {prop.enum.map((opt: string) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={prop.sensitive ? 'password' : 'text'}
                    value={settings[key] || ''}
                    onChange={(e) => setSettings(s => ({ ...s, [key]: e.target.value }))}
                    className="w-full bg-[#0b0f19] border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500 transition-colors"
                    placeholder={`Enter ${key}...`}
                  />
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#1e293b] bg-[#0b0f19]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={saveSettings}
            disabled={isSaving || !schema || Object.keys(schema).length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-md transition-colors shadow-sm"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};
